import { useEffect, useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { Popup } from '@myrealtrip/web-ui';

import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useDateSearch from '~hooks/pointToPointTicket/useDateSearch';

import type { SearchConditionPopupLocation } from '~types/pointToPointTicket';

import CalendarBody from './SearchType/Date/CalendarBody';

const { sendLayerViewLog } = helper;

interface Props {
  isOpen: boolean;
  isSingleTrip: boolean;
  departureDate: string | null;
  arrivalDate: string | null | undefined;
  showTimeOption?: boolean;
  outboundDepartureTime?: string | null;
  inboundDepartureTime?: string | null;
  popupLocation?: SearchConditionPopupLocation;
  onApply: (params: {
    startDate: string;
    outboundStartTime?: string;
    endDate?: string;
    inboundStartTime?: string;
  }) => void;
  onClose: () => void;
}

const DatePickerDropdown = ({
  isOpen,
  isSingleTrip,
  departureDate,
  arrivalDate,
  showTimeOption,
  outboundDepartureTime,
  inboundDepartureTime,
  popupLocation,
  onApply,
  onClose,
}: Props) => {
  const optionListRef = useRef<HTMLUListElement>(null);

  const {
    startDate,
    endDate,
    outboundStartTime,
    inboundStartTime,
    handleChangeSingleTripDate,
    handleChangeRoundTripDates,
    handleChangeDepartureTime,
    handleApply,
    handleReset,
  } = useDateSearch({
    isSingleTrip,
    departureDate,
    arrivalDate,
    outboundDepartureTime,
    inboundDepartureTime,
    onApply,
  });

  useEffect(() => {
    if (!isOpen) return;

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.SELECT_DATE,
      itemName: '날짜 선택',
    });
  }, [isOpen]);

  return (
    <CustomPopup
      popupLocation={popupLocation}
      visible={isOpen}
      onOutsideClick={onClose}
      insideElements={[optionListRef]}
    >
      <CalendarBody
        isSingleTrip={isSingleTrip}
        startDate={startDate}
        endDate={endDate}
        onApply={handleApply}
        onChangeDate={handleChangeSingleTripDate}
        onRangeChangeDate={handleChangeRoundTripDates}
        onReset={handleReset}
        onChangeTime={handleChangeDepartureTime}
        showTimeOption={showTimeOption}
        outboundStartTime={outboundStartTime}
        inboundStartTime={inboundStartTime}
        ref={optionListRef}
      />
    </CustomPopup>
  );
};

const CustomPopup = styled(Popup)<{ popupLocation?: SearchConditionPopupLocation }>`
  width: 440px;
  overflow-y: scroll;
  scrollbar-width: none;
  border-radius: 12px;
  ${({ popupLocation }) => popupLocation?.css && css(popupLocation.css)}
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

export default DatePickerDropdown;
